import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { getProduct, PRODUCTS } from "@/data/products";
import { ArrowRight, PackageOpen, ChevronDown, Download, Award, ShieldCheck } from "lucide-react";
import SampleRequestModal from "@/components/SampleRequestModal";
import Product360Viewer from "@/components/Product360Viewer";
import DistributorCentre from "@/components/DistributorCentre";
import SeoHead from "@/components/SeoHead";
import { useTranslation } from "react-i18next";
import { api, API } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImage = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

export default function ProductFamily() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const p = getProduct(slug);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [sampleVariant, setSampleVariant] = useState(null);
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/brands/${slug}/variants`).then((r) => setVariants(r.data.items || [])).catch(() => setVariants([]));
  }, [slug]);

  const jsonLd = useMemo(() => {
    if (!p) return null;
    const offers = variants.flatMap((v) => (v.sizes || []).map((s) => ({
      "@type": "Offer",
      name: `${v.name} · ${s.label} ${s.format}`,
      availability: "https://schema.org/InStock",
      priceCurrency: "GBP",
      areaServed: ["GB", "NG", "GH", "KE", "ZA", "UG", "TZ", "CM"],
    })));
    return {
      "@context": "https://schema.org",
      "@type": "ProductGroup",
      productGroupID: p.slug,
      name: `${p.name} — ${p.tag || "SBC beverage"}`,
      brand: { "@type": "Brand", name: p.name, logo: `${window.location.origin}/icons/icon-512.png` },
      description: p.description,
      image: variants.filter((v) => v.image_url).map((v) => absoluteImage(v.image_url)),
      manufacturer: {
        "@type": "Organization",
        name: "Scitygate Brewing Company Limited",
        legalName: "Scitygate Brewing Company Limited",
        alternateName: "SBC Ltd",
        identifier: "Company No. 17323558",
        url: "https://www.sbcltd.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "2nd Floor Halifax House, 93–101 Bridge Street",
          addressLocality: "Manchester", postalCode: "M3 2GX", addressCountry: "GB",
        },
      },
      hasVariant: variants.map((v) => ({
        "@type": "Product", name: v.name, sku: v.id,
        image: v.image_url ? absoluteImage(v.image_url) : undefined,
        description: v.label_description || v.note,
      })),
      offers,
    };
  }, [p, variants]);

  const allSizes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of variants) {
      for (const s of (v.sizes || [])) {
        const k = `${s.format}-${s.label}`;
        if (!seen.has(k)) { seen.add(k); out.push(s); }
      }
    }
    return out;
  }, [variants]);

  if (!p) return <Navigate to="/" replace />;

  const other = PRODUCTS.filter((x) => x.slug !== p.slug);

  const openSampleFor = (variant) => { setSampleVariant(variant); setSampleOpen(true); };

  return (
    <div data-testid={`product-page-${p.slug}`}>
      <SeoHead
        title={`${p.name} · ${p.tag || "SBC beverage"} — SBC Ltd Brewing`}
        description={p.description}
        canonical={`/brands/${p.slug}`}
        image={variants[0]?.image_url ? absoluteImage(variants[0].image_url) : "/icons/icon-512.png"}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className={`relative bg-gradient-to-br ${p.accentBg} text-white pt-16 pb-24 overflow-hidden`}>
        <div className="absolute inset-0 opacity-25">
          <img src={p.lifestyle} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6 space-y-6">
            <div className="eyebrow text-gold-300">{p.highlight}</div>
            <h1 className="font-display font-black text-6xl lg:text-8xl tracking-tighter leading-[0.85]">{p.name}</h1>
            <p className="text-2xl text-white/90 font-display">{p.tagline}</p>
            <p className="text-base text-white/80 leading-relaxed max-w-xl">{p.description}</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={() => openSampleFor(null)} className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-6 py-3 text-sm font-semibold transition-colors" data-testid={`request-sample-${p.slug}`}>
                <PackageOpen className="w-4 h-4" /> {t("product.request_sample")}
              </button>
              <Link to="/distributor" className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white rounded-full px-6 py-3 text-sm font-semibold transition-colors">
                {t("product.distribute")} {p.name} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#family" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold px-4 py-3 transition-colors">
                See the family <ChevronDown className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-6">
            <div className="w-full aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 animate-float bg-neutral-900">
              <img src={p.board} alt={p.name} className="w-full h-full object-cover object-center" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 bg-background" data-testid="brand-story">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-4">Brand Story</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter">Crafted with intention. Loved worldwide.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">{p.description}</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <StoryBadge Icon={Award} title="World-Class" caption="International quality standards" />
              <StoryBadge Icon={ShieldCheck} title="Food Safe" caption="HACCP + ISO 22000 aligned" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={p.board} alt="" className="rounded-2xl aspect-square object-cover w-full" />
            <img src={p.lifestyle} alt="" className="rounded-2xl aspect-square object-cover w-full mt-8" />
          </div>
        </div>
      </section>

      {/* Product Family — variant grid with images */}
      <section id="family" className="py-24 bg-muted/40" data-testid="product-family-grid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow">Product Family</div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter mt-2">The {p.name} range</h2>
            </div>
            <div className="text-sm text-muted-foreground">{variants.length} variant{variants.length !== 1 ? "s" : ""}</div>
          </div>

          {variants.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">Loading variants…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {variants.map((v) => <VariantCard key={v.id} variant={v} accent={v.accent || p.accent} onSample={() => openSampleFor(v)} />)}
            </div>
          )}
        </div>
      </section>

      {/* Available Sizes */}
      {allSizes.length > 0 && (
        <section className="py-24 bg-background" data-testid="sizes-grid">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="eyebrow">Available Sizes</div>
              <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">Sized for every occasion</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {allSizes.map((s) => (
                <div key={`${s.format}-${s.label}`} className="p-5 rounded-2xl border border-border bg-background hover:border-gold-400 transition-colors text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{s.format}</div>
                  <div className="font-display font-bold text-xl">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {p.gallery && p.gallery.length > 0 && (
        <section className="py-16 bg-muted/40" data-testid={`product-gallery-${p.slug}`}>
          <div className="max-w-6xl mx-auto px-6">
            <div className="eyebrow mb-6">Gallery</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {p.gallery.map((src, i) => (
                <div key={src} className={`relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}>
                  <img src={src} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Distributor Centre — MOQ, container loading, pallet config, docs, downloads */}
      <DistributorCentre brand={p} onRequestPriceList={() => openSampleFor(null)} />

      {/* Downloads */}
      <section className="py-16 bg-background" data-testid="brand-downloads">
        <div className="max-w-4xl mx-auto px-6">
          <div className="eyebrow mb-4">Downloads</div>
          <h2 className="font-display font-bold text-3xl tracking-tighter">{p.name} resources</h2>
          <ul className="mt-6 divide-y divide-border rounded-2xl border border-border overflow-hidden">
            {[
              { title: `${p.name} Product Catalogue`, size: "3.2 MB", type: "PDF" },
              { title: `${p.name} Nutrition Sheet`, size: "0.6 MB", type: "PDF" },
              { title: `${p.name} Distributor Brochure`, size: "1.8 MB", type: "PDF" },
              { title: `${p.name} Media Kit`, size: "12 MB", type: "ZIP" },
            ].map((d) => (
              <li key={d.title} className="flex items-center justify-between p-5">
                <div className="flex items-center gap-3">
                  <Download className="w-4 h-4 text-gold-500" />
                  <div>
                    <div className="font-display font-semibold text-sm">{d.title}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{d.type} · {d.size}</div>
                  </div>
                </div>
                <a href="#" className="text-sm text-gold-500 hover:text-gold-400 font-semibold">Download →</a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-muted/40" data-testid="brand-faqs">
        <div className="max-w-4xl mx-auto px-6">
          <div className="eyebrow mb-4">FAQs</div>
          <h2 className="font-display font-bold text-3xl tracking-tighter mb-8">Common questions</h2>
          <div className="space-y-2">
            {FAQS(p).map((f) => (
              <details key={f.q} className="p-5 rounded-xl bg-background border border-border group">
                <summary className="cursor-pointer list-none flex justify-between items-center font-semibold text-sm">
                  {f.q}
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other brands */}
      <section className="py-24 bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-10">
            <h2 className="font-display font-bold text-3xl">{t("product.family_title")}</h2>
            <Link to="/#brands" className="text-sm text-gold-400 hover:text-gold-300">{t("product.all_brands")} →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {other.map((o) => (
              <Link key={o.slug} to={`/brands/${o.slug}`} className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${o.accentBg} min-h-[220px] p-6 flex flex-col justify-end`}>
                <img src={o.board} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
                <div className="relative">
                  <div className="font-display font-black text-2xl">{o.name}</div>
                  <div className="text-xs text-white/70 mt-1">{o.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SampleRequestModal product={{ ...p, variants: sampleVariant ? [{ name: sampleVariant.name, note: sampleVariant.note || "" }] : (variants.length ? variants.map((v) => ({ name: v.name, note: v.note })) : p.variants) }} open={sampleOpen} onClose={() => setSampleOpen(false)} />
    </div>
  );
}

function useMemoSizes(variants) {
  return useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of variants) {
      for (const s of (v.sizes || [])) {
        const k = `${s.format}-${s.label}`;
        if (!seen.has(k)) { seen.add(k); out.push(s); }
      }
    }
    return out;
  }, [variants]);
}

function VariantCard({ variant, accent, onSample }) {
  const img = variant.image_url ? absoluteImage(variant.image_url) : null;
  return (
    <div className="group rounded-2xl bg-background border border-border overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`variant-card-${variant.id}`}>
      {img ? (
        <Product360Viewer imageUrl={variant.image_url} alt={variant.name} accent={variant.accent || accent} />
      ) : (
        <div className="relative aspect-square overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}20, ${accent}05)` }}>
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-6" style={{ color: accent }}>
            <div className="w-16 h-24 rounded-md border-2 flex items-end justify-center pb-2 text-[9px] font-black uppercase" style={{ borderColor: accent }}>
              {variant.packaging?.split(" ")[0] || "Pack"}
            </div>
            <div className="text-xs uppercase tracking-widest font-bold">Rendering soon</div>
          </div>
        </div>
      )}
      <div className="p-4 space-y-2">
        <div className="font-display font-bold text-base">{variant.name}</div>
        {variant.note && <div className="text-xs text-muted-foreground line-clamp-2">{variant.note}</div>}
        {variant.sizes && variant.sizes.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2">
            {variant.sizes.slice(0, 4).map((s) => (
              <span key={`${s.format}-${s.label}`} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted font-semibold">{s.label}</span>
            ))}
            {variant.sizes.length > 4 && <span className="text-[10px] text-muted-foreground">+{variant.sizes.length - 4}</span>}
          </div>
        )}
        <button onClick={onSample} className="mt-3 text-xs font-semibold text-gold-600 hover:text-gold-500 inline-flex items-center gap-1" data-testid={`variant-sample-${variant.id}`}>
          Request Sample <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function StoryBadge({ Icon, title, caption }) {
  return (
    <div className="p-4 rounded-xl border border-border">
      <Icon className="w-5 h-5 text-gold-500" />
      <div className="font-display font-bold text-sm mt-2">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{caption}</div>
    </div>
  );
}

function FAQS(p) {
  return [
    { q: `Where is ${p.name} produced?`, a: `${p.name} is produced under strict quality controls in facilities aligned to ISO 22000 and HACCP standards, coordinated from SBC Ltd's Manchester headquarters.` },
    { q: "What is the minimum order quantity?", a: "One 20ft container (FCL) is the standard MOQ for export markets. For domestic UK distribution, minimum pallet quantities apply." },
    { q: "Which markets are planned for export?", a: "SBC Ltd is in the product-development stage. Target markets under evaluation include the United Kingdom, selected African markets (such as Nigeria, Ghana, Kenya and South Africa), the Middle East (UAE, Saudi Arabia) and North Africa. Final launch markets will be confirmed once product development, manufacturing partnerships and distribution agreements are in place." },
    { q: "Is halal certification available?", a: "Yes — our portfolio is halal certified for markets that require it. Kosher and organic certifications are available upon request for selected SKUs." },
    { q: "How do I request pricing?", a: "Complete the distributor application form or click the Request Price List button above and our export team will respond within 5 business days." },
  ];
}
